import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { MIN_PASSWORD_SCORE, type PasswordStrength } from '@/lib/password'

interface Props {
  readonly strength: PasswordStrength
  readonly hasInput: boolean
}

export function PasswordStrengthMeter({ strength, hasInput }: Props) {
  const { t } = useTranslation()
  if (!hasInput) return null

  const { score, meetsLength, meetsScore, warning } = strength

  const labelKey =
    score <= 1
      ? 'setupPassword.strength.weak'
      : score === 2
        ? 'setupPassword.strength.fair'
        : score === 3
          ? 'setupPassword.strength.good'
          : 'setupPassword.strength.strong'

  const barColor =
    score <= 1 ? 'bg-red-500' : score === 2 ? 'bg-yellow-500' : score === 3 ? 'bg-green-500' : 'bg-emerald-600'

  const labelColor =
    score <= 1 ? 'text-red-500' : score === 2 ? 'text-yellow-600' : score === 3 ? 'text-green-600' : 'text-emerald-700'

  // Show the actionable hint once the user has typed something but hasn't yet
  // crossed the acceptance threshold. Prefer zxcvbn's specific warning when
  // available; otherwise fall back to a generic length/strength nudge.
  const belowThreshold = !meetsLength || !meetsScore
  const hint = belowThreshold
    ? warning ||
      (!meetsLength
        ? t('setupPassword.hint.tooShort')
        : score < MIN_PASSWORD_SCORE
          ? t('setupPassword.hint.tooEasy')
          : '')
    : ''

  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between text-xs'>
        <span className='text-muted-foreground'>{t('setupPassword.passwordStrength')}</span>
        <span className={cn('font-medium', labelColor)}>{t(labelKey)}</span>
      </div>
      <div className='flex gap-1' aria-hidden='true'>
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={cn('h-1.5 flex-1 rounded-full transition-colors', level <= score ? barColor : 'bg-gray-200')}
          />
        ))}
      </div>
      {hint && <p className='text-xs text-muted-foreground'>{hint}</p>}
    </div>
  )
}
